import * as React from "react";
import { UitkSelect } from "uitk-react-select";
import { ItemKeyHelper } from "src/components/utility/ItemKeyHelper";
import {
  UitkLayoutGridItem,
  UitkLayoutGrid,
  UitkLayoutGridSpan,
  UitkLayoutConditionalGridSpan,
} from "uitk-react-layout-grid";
import { observer } from "mobx-react";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { ThirdPartyPackageWizardState } from "src/stores/wizard/state";
import { useLocalization } from "@shared-ui/localization-context";
import { Viewport, ViewSmall, ViewMedium, Layer } from "bernie-view";
import { UitkFakeInput } from "uitk-react-input";
import { withStores } from "src/stores";
import { RouteStore, DialogRoute } from "bernie-core";
import { Locations3PPDialog, LOCATION_TYPES, LocationsListOption } from "./Locations3PPDialog";

export interface Locations3PPProps {
  colSpan?: UitkLayoutConditionalGridSpan;
  lobState: ThirdPartyPackageWizardState;
  context: ExtendedContextStore;
  routeStore: RouteStore;
}

const buildLocationParams = (
  lobState: ThirdPartyPackageWizardState,
  context: ExtendedContextStore | undefined
): { name: string; value: string | undefined }[] => {
  const { destination, origin } = lobState;

  const siteid = context?.site.id;
  const locale = context?.locale || "";
  const currency = context?.currency;

  const splittedDestinationValue = destination.split(":");
  const destinationValue = splittedDestinationValue[0];
  const destinationCityName = splittedDestinationValue[1];

  const isApiCallEnabled = siteid && lobState.config.siteIdsWithApiCall.includes(siteid);
  const params = [];
  const posNoLangParam = [4, 6, 10122006];

  if (isApiCallEnabled) {
    return [
      { name: "origin", value: origin },
      { name: "destination", value: destination },
    ];
  }

  if (siteid && !posNoLangParam.includes(siteid)) {
    params.push({ name: "lang", value: locale.replace("_", "-") });
  }

  if (siteid === 6 && destinationValue !== "0") {
    if (destinationCityName) {
      params.push({ name: "rgid", value: destinationValue }, { name: "rid", value: destinationCityName });
    } else {
      params.push({ name: "udf", value: destinationValue });
    }
  } else {
    if (destinationValue && destinationValue !== "any") {
      params.push({ name: "rgid", value: destinationValue });
    }
    if (destinationCityName) {
      params.push({ name: "rid", value: destinationCityName });
    }

    params.push({ name: "currency", value: currency });
  }

  if (origin && origin !== "any") {
    params.push({ name: "depap", value: origin });
  }

  return params;
};

export const Locations3PPComponent = observer((props: Locations3PPProps) => {
  const { formatText } = useLocalization();
  const { lobState, context, colSpan } = props;

  const locationColSpan = {
    small: 1 as UitkLayoutGridSpan,
    medium: 1 as UitkLayoutGridSpan,
  };

  const leavingKeyHelper = new ItemKeyHelper("leaving-options");
  const goingKeyHelper = new ItemKeyHelper("going-options");
  const inputKeyHelper = new ItemKeyHelper("input-options");

  /**
   * Handle selection of origin in medium and greater viewport sizes
   */
  const changeOrigin = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    lobState.setOrigin(selectEvent.target.value);
  };

  /**
   * Handle selection of destination in medium and greater viewport sizes
   */
  const changeDestination = (selectEvent: React.ChangeEvent<HTMLSelectElement>) => {
    lobState.setDestination(selectEvent.target.value);
  };

  const { destinations, origins, originInvalidKey, destinationInvalidKey } = lobState;

  const params = buildLocationParams(lobState, context);

  const leavingPlaceholder = formatText(lobState.config.origin.placeholderText);
  const goingPlaceholder = formatText(lobState.config.destination.placeholderText);

  const originDialogQueryRouteId = "threepp-origin-dialog";
  const destinationDialogQueryRouteId = "threepp-destination-dialog";
  const [originName, setOriginName] = React.useState("");
  const [destinationName, setDestinationName] = React.useState("");

  const openOriginDialog = () => {
    props.routeStore.updateSearch({ pwaDialog: originDialogQueryRouteId });
  };

  const openDestinationDialog = () => {
    props.routeStore.updateSearch({ pwaDialog: destinationDialogQueryRouteId });
  };

  const onDismissDialog = () => {
    props.routeStore.goBack();
  };

  const onOriginSelect = ({ code, name }: LocationsListOption) => {
    lobState.setOrigin(code, false);
    lobState.setDestination("");

    setOriginName(name);
    setDestinationName("");
    props.routeStore.goBack();
  };

  const onDestinationSelect = ({ code, name }: LocationsListOption) => {
    lobState.setDestination(code);

    setDestinationName(name);
    props.routeStore.goBack();
  };

  return (
    <>
      {params &&
        params.map(({ name, value }) => <input type="hidden" key={inputKeyHelper.next()} name={name} value={value} />)}
      <UitkLayoutGridItem colSpan={colSpan}>
        <UitkLayoutGrid columns={{ small: 1, medium: 2 }} space="three" className={`Locations3PP`}>
          <Viewport>
            {/* Origin and destination selector type dialog */}
            <ViewSmall>
              <>
                <UitkLayoutGridItem colSpan={locationColSpan}>
                  <div>
                    <UitkFakeInput
                      id={`${originDialogQueryRouteId}-trigger`}
                      className={`${originDialogQueryRouteId}-trigger`}
                      label={leavingPlaceholder}
                      placeholder={leavingPlaceholder}
                      value={originName}
                      onClick={openOriginDialog}
                      icon="place"
                    />
                    <DialogRoute id={originDialogQueryRouteId}>
                      <Layer id={`${originDialogQueryRouteId}-dialog`}>
                        <Locations3PPDialog
                          queryRouteId={originDialogQueryRouteId}
                          locationType={LOCATION_TYPES.origin}
                          toolbarHeader={leavingPlaceholder}
                          options={origins}
                          onDismiss={onDismissDialog}
                          onDoneButtonClick={onOriginSelect}
                        />
                      </Layer>
                    </DialogRoute>
                  </div>
                </UitkLayoutGridItem>

                <UitkLayoutGridItem colSpan={locationColSpan}>
                  <div>
                    <UitkFakeInput
                      id={`${destinationDialogQueryRouteId}-trigger`}
                      className={`${destinationDialogQueryRouteId}-trigger`}
                      label={goingPlaceholder}
                      placeholder={goingPlaceholder}
                      value={destinationName}
                      onClick={openDestinationDialog}
                      icon="place"
                    />
                    {destinations && (
                      <DialogRoute id={destinationDialogQueryRouteId}>
                        <Layer id={`${destinationDialogQueryRouteId}-dialog`}>
                          <Locations3PPDialog
                            onDismiss={onDismissDialog}
                            onDoneButtonClick={onDestinationSelect}
                            queryRouteId={destinationDialogQueryRouteId}
                            locationType={LOCATION_TYPES.destination}
                            options={destinations}
                            toolbarHeader={goingPlaceholder}
                          />
                        </Layer>
                      </DialogRoute>
                    )}
                  </div>
                </UitkLayoutGridItem>
              </>
            </ViewSmall>

            {/* Origin and destination selector type simple select */}
            <ViewMedium>
              <>
                <UitkLayoutGridItem colSpan={locationColSpan}>
                  <UitkSelect
                    icon="place"
                    id="leaving-3pp"
                    label={leavingPlaceholder}
                    placeholder={leavingPlaceholder}
                    onChange={changeOrigin}
                    invalid={formatText(originInvalidKey)}
                    value={lobState.origin || undefined}
                  >
                    {origins.map(({ code, name }) => {
                      return (
                        <option value={code} key={leavingKeyHelper.next()}>
                          {name}
                        </option>
                      );
                    })}
                  </UitkSelect>
                </UitkLayoutGridItem>

                <UitkLayoutGridItem colSpan={locationColSpan}>
                  <UitkSelect
                    icon="place"
                    id="going-3pp"
                    label={goingPlaceholder}
                    placeholder={goingPlaceholder}
                    onChange={changeDestination}
                    invalid={formatText(destinationInvalidKey)}
                    value={lobState.destination || undefined}
                  >
                    {destinations &&
                      destinations.map(({ code, name }) => {
                        return (
                          <option value={code} key={goingKeyHelper.next()}>
                            {name}
                          </option>
                        );
                      })}
                  </UitkSelect>
                </UitkLayoutGridItem>
              </>
            </ViewMedium>
          </Viewport>
        </UitkLayoutGrid>
      </UitkLayoutGridItem>
    </>
  );
});

export const Locations3PP = withStores("context", "routeStore")(Locations3PPComponent);
